/**
 * EcoDeal Error Logging Utility
 * 
 * Provides structured error logging for debugging and monitoring
 */

export interface ErrorLogContext {
  userId?: string;
  cartId?: string;
  productId?: string;
  restaurantId?: string;
  action?: string;
  timestamp?: string;
  userAgent?: string;
  [key: string]: any;
}

export interface StructuredError {
  code?: string;
  message: string;
  details?: any;
  hint?: string;
  context?: ErrorLogContext;
  stack?: string;
}

class ErrorLogger {
  private isDevelopment = import.meta.env.DEV;

  /**
   * Log structured error with context
   */
  logError(error: StructuredError): void {
    const logData = {
      ...error,
      timestamp: new Date().toISOString(),
      environment: this.isDevelopment ? 'development' : 'production'
    };

    if (this.isDevelopment) {
      console.group('🚨 EcoDeal Error');
      console.error('Message:', error.message);
      if (error.code) console.error('Code:', error.code);
      if (error.details) console.error('Details:', error.details);
      if (error.hint) console.error('Hint:', error.hint);
      if (error.context) console.error('Context:', error.context);
      if (error.stack) console.error('Stack:', error.stack);
      console.groupEnd();
    } else {
      // In production, send to monitoring service (Sentry, etc.)
      console.error('EcoDeal Error:', logData);
    }
  }

  /**
   * Log Supabase-specific errors with enhanced context
   */
  logSupabaseError(
    error: any, 
    operation: string, 
    table: string, 
    context?: ErrorLogContext
  ): void {
    this.logError({
      code: error?.code,
      message: error?.message || 'Unknown Supabase error',
      details: error?.details,
      hint: error?.hint,
      context: {
        ...context,
        operation,
        table,
        supabaseError: true
      }
    });
  }

  /**
   * Log cart-related errors
   */
  logCartError(
    error: any,
    action: string,
    cartId?: string,
    productId?: string,
    userId?: string
  ): void {
    this.logSupabaseError(error, action, 'cart_items', {
      cartId,
      productId,
      userId,
      action
    });
  }

  /**
   * Log order-related errors
   */
  logOrderError(
    error: any,
    action: string,
    orderId?: string,
    userId?: string,
    restaurantId?: string
  ): void {
    this.logSupabaseError(error, action, 'orders', {
      orderId,
      userId,
      restaurantId,
      action
    });
  }

  /**
   * Check if error is RLS (Row Level Security) related
   */
  isRLSError(error: any): boolean {
    return error?.code === '42501' || 
           error?.message?.includes('policy') ||
           error?.message?.includes('permission denied') ||
           error?.message?.includes('Not Acceptable');
  }

  /**
   * Check if error is authentication related
   */
  isAuthError(error: any): boolean {
    return error?.code === 'PGRST301' ||
           error?.message?.includes('JWT') ||
           error?.message?.includes('authentication') ||
           this.isRLSError(error);
  }

  /**
   * Get user-friendly error message
   */
  getUserFriendlyMessage(error: any, defaultMessage: string = '알 수 없는 오류가 발생했습니다.'): string {
    if (this.isAuthError(error)) {
      return '로그인이 필요합니다. 다시 로그인해주세요.';
    }

    if (error?.code === '23503') {
      return '필요한 데이터를 찾을 수 없습니다. 페이지를 새로고침해주세요.';
    }

    if (error?.code === '23514') {
      return '입력된 데이터가 올바르지 않습니다. 다시 시도해주세요.';
    }

    if (error?.code === 'PGRST116') {
      return '요청한 데이터를 찾을 수 없습니다.';
    }

    // Timestamp format errors
    if (error?.code === '22007') {
      if (error?.message?.includes('timestamp')) {
        return '날짜/시간 형식이 올바르지 않습니다. 다시 선택해주세요.';
      }
      return '입력된 시간 형식이 올바르지 않습니다.';
    }

    // Date/time related errors
    if (error?.code === '22008') {
      return '날짜/시간 값이 유효하지 않습니다.';
    }

    return error?.message || defaultMessage;
  }
}

export const errorLogger = new ErrorLogger();